import { clone, createUniqueId } from '@jack-henry/frontend-utils/functions';
import {
    DayOfWeekDto,
    FiPermissionModelDto,
    GetUsersModelDto,
    IpRestrictionAddressModelDto,
    IpRestrictionModelDto,
    RoleModelDto,
    TimeAccessTypeDto,
    UserModelDto,
    UserTimeAccessDto,
} from '@treasury/api/bo';
import { IpRestrictionAddress } from '../backoffice/services/access/entities/ip-restriction-address.entity';
import { UserTimeAccessDay } from '../backoffice/services/access/entities/user-time-access-day.entity';
import { DomainEntity } from '../shared';

export const nullFiUser: UserModelDto = Object.freeze({
    id: 0,
    userId: '',
    userUniqueId: '',
    userName: '',
    email: '',
    isActiveUser: false,
    isSuperUser: false,
    isUserAdmin: false,
    isReportsAdmin: false,
    isAccountAdmin: false,
    isLocked: false,
    isMfaBlocked: false,
    isEnrolled: false,
    enrollUser: false,
    useClientBiometricsAuthentication: false,
    backOfficeSsoEnabled: false,
    hasLoggedIn: false,
    isTokenMfaLocked: false,
});

export const defaultTimeAccessDays = Object.freeze([
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.TimeRange,
        dayOfWeek: DayOfWeekDto.Monday,
        beginTime: '08:00',
        endTime: '17:00',
    }),
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.TimeRange,
        dayOfWeek: DayOfWeekDto.Tuesday,
        beginTime: '08:00',
        endTime: '17:00',
    }),
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.TimeRange,
        dayOfWeek: DayOfWeekDto.Wednesday,
        beginTime: '08:00',
        endTime: '17:00',
    }),
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.TimeRange,
        dayOfWeek: DayOfWeekDto.Thursday,
        beginTime: '08:00',
        endTime: '17:00',
    }),
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.TimeRange,
        dayOfWeek: DayOfWeekDto.Friday,
        beginTime: '08:00',
        endTime: '17:00',
    }),
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.RestrictAllDay,
        dayOfWeek: DayOfWeekDto.Saturday,
    }),
    new UserTimeAccessDay({
        timeAccessType: TimeAccessTypeDto.RestrictAllDay,
        dayOfWeek: DayOfWeekDto.Sunday,
    }),
]);

const defaultIpRestrictionModelDto: IpRestrictionModelDto = {
    ipRestrictions: [] as IpRestrictionAddressModelDto[],
    restricted: false,
    restrictedPending: false,
    userId: 0,
};

const defaultUserTimeAccess: UserTimeAccessDto = {
    timeRestricted: false,
    userId: 0,
    userTimeAccessDays: [],
    success: true,
};

/**
 * Normalize time strings by stripping seconds (e.g., "08:00:00" -> "08:00").
 * This ensures consistency between server data and Vaadin time picker values.
 */
function normalizeTime(time?: string): string {
    return time?.replace(/^(\d{1,2}:\d{2}):\d{2}$/, '$1') ?? '';
}

/**
 * Normalize time values in userTimeAccessDays to ensure consistent format.
 */
function normalizeUserTimeAccess(userTimeAccess?: UserTimeAccessDto): UserTimeAccessDto {
    if (!userTimeAccess) {
        return defaultUserTimeAccess;
    }
    return {
        ...userTimeAccess,
        userTimeAccessDays: userTimeAccess.userTimeAccessDays?.map(day => ({
            ...day,
            beginTime: normalizeTime(day.beginTime),
            endTime: normalizeTime(day.endTime),
        })),
    };
}

export class FiUser extends DomainEntity<UserModelDto> {
    constructor(dto = clone(nullFiUser)) {
        super(dto);
    }

    public static fromGetUserById(dto: UserModelDto): FiUser {
        return new FiUser({
            backOfficeSsoEnabled: dto.backOfficeSsoEnabled ?? false,
            comments: dto.comments ?? '',
            department: dto.department ?? '',
            email: dto.email ?? '',
            fax: dto.fax ?? '',
            hasLoggedIn: dto.hasLoggedIn ?? false,
            id: dto.id ?? 0,
            ipRestriction: dto.ipRestriction ?? defaultIpRestrictionModelDto,
            isAccountAdmin: dto.isAccountAdmin ?? false,
            isActiveUser: dto.isActiveUser ?? false,
            isEnrolled: dto.isEnrolled ?? false,
            isLocked: dto.isLocked ?? false,
            isMfaBlocked: dto.isMfaBlocked ?? false,
            isReportsAdmin: dto.isReportsAdmin ?? false,
            isSuperUser: dto.isSuperUser ?? false,
            isTokenMfaLocked: dto.isTokenMfaLocked ?? false,
            isUserAdmin: dto.isUserAdmin ?? false,
            mfaId: dto.mfaId ?? '',
            phoneExtension: dto.phoneExtension ?? '',
            phoneNumber: dto.phoneNumber ?? '',
            roles: dto.roles ?? [],
            status: dto.status ?? '',
            userId: dto.userId ?? '',
            userName: dto.userName ?? '',
            userTimeAccess: normalizeUserTimeAccess(dto.userTimeAccess),

            // these properties are not provided in the GetUserById response
            userUniqueId: '',
            enrollUser: false,
            useClientBiometricsAuthentication: false,
        });
    }

    public static fromGetUsersModelDto(dto: GetUsersModelDto): FiUser[] {
        const { users, backOfficeSsoEnabled } = dto;

        return (
            users?.map(
                user =>
                    new FiUser({
                        backOfficeSsoEnabled: backOfficeSsoEnabled ?? false,
                        department: user.department ?? '',
                        email: user.email ?? '',
                        fax: user.fax ?? '',
                        id: user.id,
                        isAccountAdmin: user.isAccountAdmin ?? false,
                        isActiveUser: user.isActiveUser ?? false,
                        isEnrolled: user.isEnrolled ?? false,
                        isLocked: user.isLocked ?? false,
                        isMfaBlocked: user.isMfaBlocked ?? false,
                        isReportsAdmin: user.isReportsAdmin ?? false,
                        isSuperUser: user.isSuperUser ?? false,
                        isTokenMfaLocked: user.isTokenMfaLocked ?? false,
                        isUserAdmin: user.isUserAdmin ?? false,
                        phoneExtension: user.phoneExtension ?? '',
                        phoneNumber: user.phoneNumber ?? '',
                        roles: user.roles,
                        status: user.status ?? '',
                        userId: user.alias ?? '',
                        userName: user.userName ?? '',

                        // these properties are not provided in GetUsersModelDto
                        userUniqueId: '',
                        comments: '',
                        enrollUser: false,
                        useClientBiometricsAuthentication: false,
                        hasLoggedIn: false,
                    })
            ) ?? []
        );
    }

    public createDefault(): this {
        return new FiUser(nullFiUser) as this;
    }

    public get userName() {
        return this.dto.userName;
    }

    public set userName(value: string) {
        this.dto.userName = value;
    }

    public get userNameAndId() {
        return `${this.userName} (${this.userId})`;
    }

    public get userId() {
        return this.dto.userId ?? '';
    }

    public set userId(value: string) {
        this.dto.userId = value;
    }

    public get roleNames(): string[] {
        return this.roles?.map(role => role.name) ?? [];
    }

    public get roles(): Role[] {
        return this.dto.roles?.map(role => new Role(role)) ?? [];
    }

    public set roles(value: Role[]) {
        this.dto.roles = value.map(role => role.toDto());
    }

    public get status() {
        return this.dto.status;
    }

    public get isActiveUser() {
        return this.dto.isActiveUser;
    }

    public set isActiveUser(value: boolean) {
        this.dto.isActiveUser = value;
    }

    public get isLocked() {
        return this.dto.isLocked;
    }

    public set isLocked(value: boolean) {
        this.dto.isLocked = value;
    }

    public get isEnrolled() {
        return this.dto.isEnrolled;
    }

    public get isMfaLocked() {
        return this.dto.isTokenMfaLocked;
    }

    public get isTokenMfaLocked() {
        return this.dto.isTokenMfaLocked;
    }

    public get id() {
        return this.dto.id;
    }

    public get email() {
        return this.dto.email;
    }

    public set email(value: string) {
        this.dto.email = value;
    }

    public get phoneNumber() {
        return this.dto.phoneNumber ?? '';
    }

    public set phoneNumber(value: string) {
        this.dto.phoneNumber = value;
    }

    public get department() {
        return this.dto.department ?? '';
    }

    public set department(value: string) {
        this.dto.department = value;
    }

    public get rsaId() {
        return this.dto.mfaId ?? '';
    }

    public get phoneExtension() {
        return this.dto.phoneExtension ?? '';
    }

    public set phoneExtension(value: string) {
        this.dto.phoneExtension = value;
    }

    public get fax() {
        return this.dto.fax ?? '';
    }

    public set fax(value: string) {
        this.dto.fax = value;
    }

    public get ipRestrictions(): IpRestrictionAddress[] {
        return (
            this.dto.ipRestriction?.ipRestrictions?.map(ip => {
                return new IpRestrictionAddress(ip);
            }) ?? []
        );
    }

    public set ipRestrictions(value: IpRestrictionAddress[]) {
        this.dto.ipRestriction = {
            ipRestrictions: value.map(ip => ip.toDto()),
            userId: this.dto.id,
            restricted: this.dto.ipRestriction?.restricted ?? false,
            restrictedPending: false,
        };
    }

    public get ipRestricted() {
        return this.dto.ipRestriction?.restricted ?? false;
    }

    public set ipRestricted(value: boolean) {
        if (!this.dto.ipRestriction) {
            this.dto.ipRestriction = defaultIpRestrictionModelDto;
        }

        this.dto.ipRestriction.restricted = value;
    }

    public get timeAccessDays() {
        if ((this.dto.userTimeAccess?.userTimeAccessDays ?? []).length === 0) {
            return [...defaultTimeAccessDays];
        }
        return (
            this.dto.userTimeAccess?.userTimeAccessDays?.map(day => {
                return new UserTimeAccessDay(day);
            }) ?? []
        );
    }

    public set timeAccessDays(value: UserTimeAccessDay[]) {
        this.dto.userTimeAccess = {
            userId: this.dto.id,
            timeRestricted: this.dto.userTimeAccess?.timeRestricted ?? false,
            userTimeAccessDays: value.map(day => day.toDto()),
            success: true,
        };
    }

    public get timeAccessRestricted() {
        return this.dto.userTimeAccess?.timeRestricted ?? false;
    }

    public set timeAccessRestricted(value: boolean) {
        if (!this.dto.userTimeAccess) {
            this.dto.userTimeAccess = {
                userId: this.dto.id,
                timeRestricted: false,
                userTimeAccessDays: [],
                success: true,
            };
        }

        this.dto.userTimeAccess.timeRestricted = value;
    }

    public get enrollUser() {
        return this.dto.enrollUser ?? false;
    }

    public set enrollUser(value: boolean) {
        this.dto.enrollUser = value;
    }

    public get profile(): string {
        if (!this.dto.isEnrolled) {
            return 'Not Enrolled';
        }
        if (this.isActiveUser) {
            return 'Active';
        }
        return 'Deactivated';
    }

    public toSaveDto(): Omit<UserModelDto, 'userUniqueId'> {
        return {
            backOfficeSsoEnabled: this.dto.backOfficeSsoEnabled ?? false,
            department: this.dto.department,
            email: this.dto.email,
            enrollUser: this.dto.enrollUser ?? false,
            fax: this.dto.fax,
            hasLoggedIn: this.dto.hasLoggedIn ?? false,
            id: this.dto.id,
            ipRestriction: this.dto.ipRestriction,
            isAccountAdmin: this.dto.isAccountAdmin,
            isActiveUser: this.dto.isActiveUser,
            isEnrolled: this.dto.isEnrolled,
            isLocked: this.dto.isLocked,
            isMfaBlocked: this.dto.isMfaBlocked,
            isReportsAdmin: this.dto.isReportsAdmin,
            isSuperUser: this.dto.isSuperUser,
            isTokenMfaLocked: this.dto.isTokenMfaLocked,
            isUserAdmin: this.dto.isUserAdmin,
            phoneExtension: this.dto.phoneExtension,
            phoneNumber: this.dto.phoneNumber,
            roles: this.dto.roles ?? [],
            status: this.dto.status,
            useClientBiometricsAuthentication: this.dto.useClientBiometricsAuthentication ?? false,
            userId: this.userId,
            userName: this.dto.userName,
            userTimeAccess: this.dto.userTimeAccess ?? defaultUserTimeAccess,
        };
    }

    public toFiUserProfileFormModel() {
        return {
            id: this.id,
            userName: this.userName,
            email: this.email,
            userId: this.userId,
            phoneNumber: this.phoneNumber,
            department: this.department,
            rsaId: this.rsaId,
            phoneExtension: this.phoneExtension,
            fax: this.fax,
            enrollUser: this.enrollUser,
            isActiveUser: this.isActiveUser,
            ipRestriction: {
                restricted: this.ipRestricted,
                ipRestrictions: this.ipRestrictions.map(ip => {
                    const [octet1, octet2, octet3, octet4] = ip.octets;
                    return {
                        id: ip.id,
                        name: ip.name,
                        octet1,
                        octet2,
                        octet3,
                        octet4,
                    };
                }),
            },
            timeAccess: {
                timeRestricted: this.timeAccessRestricted,
                userTimeAccessDays: this.timeAccessDays.map(day => ({
                    timeAccessType: day.timeAccessType,
                    dayOfWeek: day.dayOfWeek,
                    beginTime: day.beginTime,
                    endTime: day.endTime,
                })),
            },
            roles: this.roles.map(role => role.id),
        };
    }
}

export class Role extends DomainEntity<RoleModelDto> {
    constructor(
        dto: RoleModelDto = clone({
            id: 0,
            name: '',
        })
    ) {
        super(dto);
    }

    private readonly _id = createUniqueId();

    public createDefault(): this {
        return new Role() as this;
    }

    public get id(): string {
        return this._id;
    }

    public get originalId(): number {
        return this.dto.id ?? 0;
    }

    public get name(): string {
        return this.dto.name ?? '';
    }

    public set name(value: string) {
        this.dto.name = value;
    }

    public get description(): string {
        return this.dto.description ?? '';
    }

    public set description(value: string) {
        this.dto.description = value;
    }

    public get entitlements(): RolePermission[] {
        return this.dto.permissions?.map(permission => new RolePermission(permission)) ?? [];
    }

    public set entitlements(value: RolePermission[]) {
        this.dto.permissions = value.map(permission => permission.toDto());
    }

    public get userCount(): number {
        return this.dto.users?.split(',').length ?? 0;
    }

    public get users(): string[] {
        return this.dto.users?.split(',') ?? [];
    }
}

export class RolePermission extends DomainEntity<FiPermissionModelDto> {
    constructor(
        dto: FiPermissionModelDto = clone({
            id: 0,
            isDeleted: false,
        })
    ) {
        super(dto);
    }

    public createDefault(): this {
        return new RolePermission() as this;
    }

    public get id(): number {
        return this.dto.id ?? 0;
    }

    public get name(): string {
        return this.dto.name ?? '';
    }

    public get description(): string {
        return this.dto.description ?? '';
    }
}
